import React, { useState, useEffect, useCallback } from 'react';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { createPortal } from 'react-dom';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Button,
  IconButton,
  Divider,
  ButtonGroup
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import FlightTile from './FlightTile';
import DroppableColumn from './DroppableColumn';
import PrintDispatchButton from './PrintDispatchButton';
import { getConnectionCategory, isOvernightFlight, calculateConnectionTime } from '../../utils/connectionUtils';

const isHotOrPriority = (flight, params) => {
    const category = flight.connectionCategory;
    return category === 'hot' || category === 'priority';
};

// Helper constants for auto-assignment logic
const sectors = {
  sector1: Array.from({ length: 17 }, (_, i) => i + 23),
  sector2: Array.from({ length: 16 }, (_, i) => i + 40),
  sector3: Array.from({ length: 12 }, (_, i) => i + 56)
};
const quadrants = {
  nw: [23, 25, 27, 29, 31, 33, 35, 37, 39, 41, 43],
  sw: [24, 26, 28, 30, 32, 34, 36, 38, 40, 42, 44, 46],
  ne: [45, 47, 49, 51, 53, 55, 57, 61, 63, 65, 67],
  se: [48, 50, 52, 54, 56, 58, 60, 62, 64, 66]
};

function DispatchBoard({ tdrData, brockData, params, inboundETA }) {
  const [runs, setRuns] = useState({
    unassigned: [],
    drivers: []
  });

  // State for drag overlay
  const [activeFlight, setActiveFlight] = useState(null);

  useEffect(() => {
    if (!tdrData || !brockData) {
      setRuns({ unassigned: [], drivers: [] });
      return;
    }

    const normalizeFlightNumber = (flightNum) => {
      if (!flightNum) return '';
      const match = flightNum.match(/WN\s*(\d+)/i);
      if (match && match[1]) {
        return parseInt(match[1], 10).toString();
      }
      return flightNum;
    };

    // --- USING THE CORRECT MERGE LOGIC FROM CombinedTab.js ---
    const mergeAndProcessData = () => {
      const tdrOutbound = tdrData.outbound || [];
      const brockBins = brockData.bins || {};
      const flightMap = new Map();

      // 1) Process TDR data to build a map of flights
      tdrOutbound.forEach(flight => {
        const normalizedFlightNumber = normalizeFlightNumber(flight.flightNumber);
        flightMap.set(normalizedFlightNumber, {
            ...flight, // Keep all original TDR data
            flightNumber: normalizedFlightNumber,
            tdrBags: flight.bags,
            brockBags: 0,
            binDistribution: {},
            freightByBin: {},
            isMultiDestination: flight.destinations && flight.destinations.length > 1,
        });
      });

      // 2) Iterate through Brock data to update the flights in the map
      Object.entries(brockBins).forEach(([binName, binData]) => {
        (binData.flights || []).forEach(brockFlight => {
          const normalizedFlightNumber = normalizeFlightNumber(brockFlight.flightNumber);
          if (flightMap.has(normalizedFlightNumber)) {
            const existingFlight = flightMap.get(normalizedFlightNumber);
            
            // This logic correctly handles single and multi-destination flights
            const brockDest = brockFlight.dest;
            const isForThisDest = !existingFlight.isMultiDestination || (existingFlight.destinations || []).some(d => d.city === brockDest);

            if (isForThisDest) {
                // Find the specific destination leg to update, or update the main flight
                let targetDest = existingFlight;
                if(existingFlight.isMultiDestination){
                    targetDest = (existingFlight.destinations || []).find(d => d.city === brockDest) || existingFlight;
                }
                
                // We will add the bin data directly to the main flight object for simplicity on the board
                existingFlight.binDistribution[binName] = (existingFlight.binDistribution[binName] || 0) + brockFlight.bags;
                existingFlight.brockBags += brockFlight.bags;
                if(brockFlight.freight > 0) {
                    existingFlight.freightByBin[binName] = (existingFlight.freightByBin[binName] || 0) + brockFlight.freight;
                }
            }
          }
        });
      });
      
      // 3) Finalize data for each flight tile
      const finalFlights = [];
      flightMap.forEach((flight, key) => {
        const isOvernight = isOvernightFlight(flight);
        const connectionTime = calculateConnectionTime(flight.etd, inboundETA, isOvernight);
        const category = getConnectionCategory(connectionTime, params, isOvernight);
        
        const allTdrFreight = (flight.destinations || []).length > 0 ? (flight.destinations || []).flatMap(d => d.freight || []) : (flight.freight || []);
        
        const hasBags = (flight.bags || 0) > 0;
        const hasFreight = allTdrFreight.length > 0 || Object.values(flight.freightByBin || {}).some(v => v > 0);
        let finalDestination = flight.destination;
        if(hasFreight && !hasBags){
            finalDestination = `${flight.destination} (Freight)`;
        }

        finalFlights.push({
            ...flight,
            destination: finalDestination,
            allFreight: allTdrFreight,
            uniqueId: `${key}-${flight.destination}-0`,
            originalId: `${key}-${flight.destination}`,
            connectionTime,
            connectionCategory: category,
            isOvernight,
        });
      });
      
      return finalFlights;
    };

    const processedFlights = mergeAndProcessData();

    const sortedFlights = processedFlights.sort((a, b) => {
      const aIsHot = isHotOrPriority(a, params);
      const bIsHot = isHotOrPriority(b, params);
      if (aIsHot && !bIsHot) return -1;
      if (!aIsHot && bIsHot) return 1;
      return (a.etd || '').localeCompare(b.etd || '');
    });

    setRuns({
      unassigned: sortedFlights,
      drivers: []
    });
  }, [tdrData, brockData, params, inboundETA]);

  // *** THIS FUNCTION IS UPDATED ***
  const handleAddDriver = () => {
    setRuns(prevRuns => {
      // This logic for getting a unique ID is still correct.
      const newDriverId = (prevRuns.drivers.length > 0) ? Math.max(...prevRuns.drivers.map(d => d.id)) + 1 : 1;
      
      const newDriver = { id: newDriverId, name: `Driver ${newDriverId}`, flights: [] };

      // Prepend the new driver to the start of the array instead of appending it to the end.
      return { 
        ...prevRuns, 
        drivers: [newDriver, ...prevRuns.drivers] 
      };
    });
  };

  const handleRemoveDriver = (driverIdToRemove) => {
    setRuns(prevRuns => {
      const driverToRemove = prevRuns.drivers.find(d => d.id === driverIdToRemove);
      const flightsToReassign = driverToRemove ? driverToRemove.flights : [];
      
      // All flights from the removed driver are now considered re-assignable.
      const reassignableFlights = flightsToReassign;
      
      const remainingDrivers = prevRuns.drivers.filter(d => d.id !== driverIdToRemove);
      const newUnassigned = [...prevRuns.unassigned, ...reassignableFlights].sort((a, b) => {
        const aIsHot = isHotOrPriority(a, params);
        const bIsHot = isHotOrPriority(b, params);
        if (aIsHot && !bIsHot) return -1;
        if (!aIsHot && bIsHot) return 1;
        return (a.etd || '').localeCompare(b.etd || '');
      });
      return {
        unassigned: newUnassigned,
        drivers: remainingDrivers,
      };
    });
  };

  // NOTE: This function is not used since the 'X' on the tile was removed, but it's fine to leave it.
  const handleRemoveFlight = useCallback((flightToRemoveId) => {
    setRuns(prevRuns => {
      let flightToReassign = null;
      
      const updatedDrivers = prevRuns.drivers.map(driver => {
        const flightIndex = driver.flights.findIndex(f => f.uniqueId === flightToRemoveId);
        
        if (flightIndex > -1) {
          flightToReassign = driver.flights[flightIndex];
          return {
            ...driver,
            flights: driver.flights.filter(f => f.uniqueId !== flightToRemoveId),
          };
        }
        return driver;
      });

      if (!flightToReassign) {
        return prevRuns;
      }

      const updatedUnassigned = [...prevRuns.unassigned, flightToReassign].sort((a, b) => {
        const aIsHot = isHotOrPriority(a, params);
        const bIsHot = isHotOrPriority(b, params);
        if (aIsHot && !bIsHot) return -1;
        if (!aIsHot && bIsHot) return 1;
        return (a.etd || '').localeCompare(b.etd || '');
      });

      return {
        unassigned: updatedUnassigned,
        drivers: updatedDrivers,
      };
    });
  }, [params]);

  const handleDragStart = useCallback((event) => {
    const { active } = event;
    const allFlights = [...runs.unassigned, ...runs.drivers.flatMap(d => d.flights)];
    const flight = allFlights.find(f => f.uniqueId === active.id);
    setActiveFlight(flight);
  }, [runs]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    setActiveFlight(null);

    if (!over) return;
    const activeId = active.id;
    const overId = over.id;
    const sourceColumnId = active.data.current?.sourceColumnId;
    if (sourceColumnId === overId) return;

    setRuns(prev => {
      let sourceList;
      if (sourceColumnId === 'unassigned') {
        sourceList = prev.unassigned;
      } else {
        sourceList = prev.drivers.find(d => `driver-${d.id}` === sourceColumnId)?.flights;
      }

      const flightIndex = sourceList?.findIndex(f => f.uniqueId === activeId);
      const movedFlight = flightIndex > -1 ? sourceList[flightIndex] : null;

      if (!movedFlight) return prev;

      const newRuns = {
        unassigned: [...prev.unassigned],
        drivers: prev.drivers.map(d => ({ ...d, flights: [...d.flights] })),
      };

      if (sourceColumnId === 'unassigned') {
        newRuns.unassigned = newRuns.unassigned.filter(f => f.uniqueId !== activeId);
      } else {
        const driver = newRuns.drivers.find(d => `driver-${d.id}` === sourceColumnId);
        if (driver) driver.flights = driver.flights.filter(f => f.uniqueId !== activeId);
      }

      if (overId === 'unassigned') {
        newRuns.unassigned.push(movedFlight);
        newRuns.unassigned.sort((a, b) => {
            const aIsHot = isHotOrPriority(a, params);
            const bIsHot = isHotOrPriority(b, params);
            if (aIsHot && !bIsHot) return -1;
            if (!aIsHot && bIsHot) return 1;
            return (a.etd || '').localeCompare(b.etd || '');
        });
      } else {
        const destDriver = newRuns.drivers.find(d => `driver-${d.id}` === overId);
        if (destDriver) {
            destDriver.flights.push(movedFlight);
            destDriver.flights.sort((a, b) => (a.etd || '').localeCompare(b.etd || ''));
        }
      }

      return newRuns;
    });
  }, [params]);

  const handleAutoAssign = useCallback((assignType) => {
    setRuns(prev => {
      let allUnassigned = [...prev.unassigned];
      let currentDrivers = [...prev.drivers];
      
      if (assignType === 'UNASSIGN_ALL') {
        const allDriverFlights = currentDrivers.flatMap(d => d.flights);
        const newUnassigned = [...allUnassigned, ...allDriverFlights].sort((a, b) => {
            const aIsHot = isHotOrPriority(a, params);
            const bIsHot = isHotOrPriority(b, params);
            if (aIsHot && !bIsHot) return -1;
            if (!bIsHot && bIsHot) return 1;
            return (a.etd || '').localeCompare(b.etd || '');
        });
        return { drivers: [], unassigned: newUnassigned };
      }

      const createDriversFromChunks = (chunks, runNamePrefix) => {
        let nextDriverId = (currentDrivers.length > 0) ? Math.max(...currentDrivers.map(d => d.id)) + 1 : 1;
        chunks.forEach((chunk, index) => {
          const driverName = chunks.length > 1 ? `${runNamePrefix} ${index + 1}` : runNamePrefix;
          const newDriver = { id: nextDriverId++, name: driverName, flights: chunk };
          currentDrivers.unshift(newDriver);
        });
      };
      
      if (assignType === 'HOT' || assignType === 'PRIORITY') {
        const categoryToTarget = assignType === 'HOT' ? 'hot' : 'priority';
        const stopLimit = assignType === 'HOT' ? 3 : 6;
        const runName = assignType === 'HOT' ? 'Hot Bag Run' : 'Priority Run';
        const targetFlights = allUnassigned
          .filter(f => f.connectionCategory === categoryToTarget)
          .sort((a, b) => (a.etd || '').localeCompare(b.etd || ''));
        if (targetFlights.length === 0) return prev;
        const remainingFlights = allUnassigned.filter(f => !targetFlights.some(t => t.uniqueId === f.uniqueId));
        const chunks = [];
        for (let i = 0; i < targetFlights.length; i += stopLimit) {
            chunks.push(targetFlights.slice(i, i + stopLimit));
        }
        createDriversFromChunks(chunks, runName);
        return { unassigned: remainingFlights, drivers: currentDrivers };
      }

      if (['ODDS_EVENS', 'EAST_WEST', 'SECTORS', 'QUADRANTS', 'COLD'].includes(assignType)) {
        let breakdowns = {};
        const getGateNum = (flight) => flight.gate ? parseInt(flight.gate.replace('C',''), 10) : NaN;
        if (assignType === 'COLD') breakdowns['Cold Run'] = allUnassigned.filter(f => f.connectionCategory === 'cold');
        if (assignType === 'ODDS_EVENS') {
          breakdowns['Odds (North)'] = allUnassigned.filter(f => getGateNum(f) % 2 !== 0);
          breakdowns['Evens (South)'] = allUnassigned.filter(f => getGateNum(f) % 2 === 0);
        }
        if (assignType === 'EAST_WEST') {
          breakdowns['West Run (C23-C47)'] = allUnassigned.filter(f => getGateNum(f) <= 47);
          breakdowns['East Run (C48-C67)'] = allUnassigned.filter(f => getGateNum(f) >= 48);
        }
        if (assignType === 'SECTORS') {
          breakdowns['Sector 1 (23-39)'] = allUnassigned.filter(f => sectors.sector1.includes(getGateNum(f)));
          breakdowns['Sector 2 (40-55)'] = allUnassigned.filter(f => sectors.sector2.includes(getGateNum(f)));
          breakdowns['Sector 3 (56-67)'] = allUnassigned.filter(f => sectors.sector3.includes(getGateNum(f)));
        }
        if (assignType === 'QUADRANTS') {
          breakdowns['North West'] = allUnassigned.filter(f => quadrants.nw.includes(getGateNum(f)));
          breakdowns['South West'] = allUnassigned.filter(f => quadrants.sw.includes(getGateNum(f)));
          breakdowns['North East'] = allUnassigned.filter(f => quadrants.ne.includes(getGateNum(f)));
          breakdowns['South East'] = allUnassigned.filter(f => quadrants.se.includes(getGateNum(f)));
        }
        const allAssignedIds = new Set(Object.values(breakdowns).flat().map(f => f.uniqueId));
        const remaining = allUnassigned.filter(f => !allAssignedIds.has(f.uniqueId));
        let nextDriverId = (currentDrivers.length > 0) ? Math.max(...currentDrivers.map(d => d.id)) + 1 : 1;
        for (const [name, flights] of Object.entries(breakdowns)) {
          if (flights.length > 0) {
            currentDrivers.unshift({ id: nextDriverId++, name, flights: flights.sort((a,b)=>(a.etd||'').localeCompare(b.etd||'')) });
          }
        }
        return { unassigned: remaining, drivers: currentDrivers };
      }
      
      if (assignType === 'SPLIT_ALL_FREIGHT') {
        const newlySplit = [];
        const alreadyFine = [];
        allUnassigned.forEach(f => {
          if ((f.bags > 0) && (f.allFreight.length > 0) && !f.isSplit) {
            newlySplit.push({ ...f, uniqueId: `${f.uniqueId}-bags`, isSplit: true, allFreight: [], freightByBin: {} });
            newlySplit.push({ ...f, uniqueId: `${f.uniqueId}-freight`, isSplit: true, bags: 0, binDistribution: {}, destination: `${f.destination.replace(' (Freight)', '')} (Freight)` });
          } else {
            alreadyFine.push(f);
          }
        });
        const finalUnassigned = [...alreadyFine, ...newlySplit].sort((a, b) => {
            const aIsHot = isHotOrPriority(a, params);
            const bIsHot = isHotOrPriority(b, params);
            if (aIsHot && !bIsHot) return -1;
            if (!bIsHot && bIsHot) return 1;
            return (a.etd || '').localeCompare(b.etd || '');
        });
        return { ...prev, unassigned: finalUnassigned };
      }
      
      if (assignType === 'ASSIGN_FREIGHT_ONLY') {
        const freightOnlyFlights = allUnassigned.filter(f => (f.bags === 0) && (f.allFreight.length > 0));
        const remaining = allUnassigned.filter(f => !freightOnlyFlights.some(t => t.uniqueId === f.uniqueId));
        if (freightOnlyFlights.length > 0) {
            createDriversFromChunks([freightOnlyFlights.sort((a,b) => (a.etd||'').localeCompare(b.etd||''))], 'Freight Run');
        }
        return { drivers: currentDrivers, unassigned: remaining };
      }
      
      if (assignType === 'COMBINE_ALL_FREIGHT') {
          const combinedFlightsMap = new Map();
          const remainingFlights = [];

          allUnassigned.forEach(flight => {
              if (flight.isSplit) {
                  const baseId = flight.originalId;
                  if (!combinedFlightsMap.has(baseId)) {
                      // Start with the first split flight as the base, but we'll restore original data
                      combinedFlightsMap.set(baseId, { ...flight });
                  }
                  const combinedFlight = combinedFlightsMap.get(baseId);
                  
                  // Only collect bags and freight from their respective runs
                  if (flight.bags > 0) {
                      // This is the bag run - use its bag data
                      combinedFlight.bags = flight.bags;
                      combinedFlight.binDistribution = { ...flight.binDistribution };
                  }
                  if (flight.allFreight && flight.allFreight.length > 0) {
                      // This is the freight run - use its freight data  
                      combinedFlight.allFreight = [...flight.allFreight];
                      combinedFlight.freightByBin = { ...flight.freightByBin };
                  }
              } else {
                  remainingFlights.push(flight);
              }
          });

          if (combinedFlightsMap.size === 0) return prev;

          const mergedList = Array.from(combinedFlightsMap.values()).map(f => ({
              ...f,
              isSplit: false,
              destination: f.destination.replace(' (Freight)', ''),
              uniqueId: f.originalId + '-combined-' + Date.now()
          }));
          
          const finalUnassigned = [...remainingFlights, ...mergedList].sort((a, b) => {
            const aIsHot = isHotOrPriority(a, params);
            const bIsHot = isHotOrPriority(b, params);
            if (aIsHot && !bIsHot) return -1;
            if (!bIsHot && bIsHot) return 1;
            return (a.etd || '').localeCompare(b.etd || '');
          });

          return { ...prev, unassigned: finalUnassigned };
      }

      return prev;
    });
  }, [params, brockData, tdrData]);

  return (
    <Accordion
      defaultExpanded
      sx={{
        backgroundColor: '#1f2937',
        color: '#e2e2e2',
        mb: 4,
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />}>
        <Typography variant="h5">Flight Dispatch Board</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ p: 2, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2 }}>
        <Box sx={{ p: 2, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'center' }}>
            {/* Manual Driver & Print */}
            <Button
                variant="outlined"
                onClick={handleAddDriver}
                sx={{ borderColor: '#4CAF50', color: '#4CAF50', '&:hover': { borderColor: '#45a049', backgroundColor: 'rgba(76, 175, 80, 0.1)' } }}
            >
                + Add Driver
            </Button>
            <PrintDispatchButton runs={runs} tdrData={tdrData} brockData={brockData} disabled={runs.drivers.every(d => d.flights.length === 0)} />
            
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx:1 }} />

            {/* Priority & Hot Bags */}
            <Button onClick={() => handleAutoAssign('HOT')} variant="contained" size="small" sx={{ backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#c62828'} }}>Assign Hot Bags</Button>
            <Button onClick={() => handleAutoAssign('PRIORITY')} variant="contained" size="small" sx={{ backgroundColor: '#F9B612', color: '#000', fontWeight: 'bold', '&:hover': { backgroundColor: '#e6a700'} }}>Assign Priority Bags</Button>
            
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx:1 }} />

            {/* Freight Management */}
            <Button onClick={() => handleAutoAssign('SPLIT_ALL_FREIGHT')} variant="contained" size="small" sx={{ backgroundColor: '#6c757d', '&:hover': { backgroundColor: '#5a6268' } }}>Split All Freight</Button>
            <Button onClick={() => handleAutoAssign('ASSIGN_FREIGHT_ONLY')} variant="contained" size="small" sx={{ backgroundColor: '#6c757d', '&:hover': { backgroundColor: '#5a6268' } }}>Assign Freight Runs</Button>
            <Button onClick={() => handleAutoAssign('COMBINE_ALL_FREIGHT')} variant="contained" size="small" sx={{ backgroundColor: '#6c757d', '&:hover': { backgroundColor: '#5a6268' } }}>Combine All Freight</Button>

            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx:1 }} />
            
            {/* Location Runs */}
            <Button onClick={() => handleAutoAssign('ODDS_EVENS')} variant="contained" size="small" color="primary">Odds/Evens</Button>
            <Button onClick={() => handleAutoAssign('EAST_WEST')} variant="contained" size="small" color="primary">East/West</Button>
            <Button onClick={() => handleAutoAssign('SECTORS')} variant="contained" size="small" color="primary">Sectors</Button>
            <Button onClick={() => handleAutoAssign('QUADRANTS')} variant="contained" size="small" color="primary">Quadrants</Button>
            <Button onClick={() => handleAutoAssign('COLD')} variant="contained" size="small" color="primary">Cold Bags</Button>
            
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx:1 }} />

            {/* Unassign All */}
            <Button onClick={() => handleAutoAssign('UNASSIGN_ALL')} variant="contained" size="small" color="error" sx={{ backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#c62828' } }} disabled={runs.drivers.length === 0}>Unassign All</Button>
        </Box>
        </Box>

        <DndContext
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {/* Horizontal Scroll Container */}
          <Box sx={{
            overflowX: 'auto',
            overflowY: 'hidden',
            width: '100%',
            paddingBottom: '12px', // Space for horizontal scrollbar
            '&::-webkit-scrollbar': {
              height: '12px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderRadius: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(255,255,255,0.3)',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.5)',
              },
            },
          }}>
            {/* Grid Layout with Fixed Column Widths */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: runs.drivers.length === 0
                ? '320px'
                : `320px repeat(${runs.drivers.length}, 320px)`,
              gap: 2,
              minWidth: 'min-content',
              minHeight: '600px',
              width: 'max-content', // Allow grid to expand beyond container
            }}>

              {/* Fixed-width Unassigned Flights Column */}
              <Box sx={{
                backgroundColor: 'rgba(0,0,0,0.3)',
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                border: '2px solid rgba(255,255,255,0.1)'
              }}>
                <Typography
                  variant="h6"
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    fontWeight: 'bold'
                  }}
                >
                  Unassigned Flights ({runs.unassigned.length})
                </Typography>
                <Box sx={{
                  overflowY: 'auto',
                  flexGrow: 1,
                  p: 1,
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.5)',
                    },
                  },
                }}>
                  <DroppableColumn
                    id="unassigned"
                    title=""
                    flights={runs.unassigned}
                    params={params}
                  />
                </Box>
              </Box>

              {/* Fixed-width Driver Columns */}
              {runs.drivers.map(driver => (
                <Box
                  key={driver.id}
                  sx={{
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    border: '2px solid rgba(255,255,255,0.1)'
                  }}
                >
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1.5,
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 'bold',
                          fontSize: '0.95rem',
                          lineHeight: 1.2,
                          mb: 0.25
                        }}
                      >
                        {driver.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.75rem',
                          color: 'rgba(255,255,255,0.8)',
                          lineHeight: 1
                        }}
                      >
                        {driver.flights.length} Stops / {driver.flights.reduce((total, flight) => total + (flight.bags || 0), 0)} Bags
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={() => handleRemoveDriver(driver.id)}
                      size="small"
                      sx={{
                        color: 'rgba(255,255,255,0.4)',
                        '&:hover': {
                          color: '#FF0000',
                          backgroundColor: 'rgba(255,0,0,0.1)'
                        }
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{
                    overflowY: 'auto',
                    flexGrow: 1,
                    p: 1,
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(255,255,255,0.3)',
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.5)',
                      },
                    },
                  }}>
                    <DroppableColumn
                      id={`driver-${driver.id}`}
                      title=""
                      flights={driver.flights}
                      params={params}
                      onRemoveFlight={handleRemoveFlight}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Use createPortal to render overlay at document body level */}
          {activeFlight && createPortal(
            <DragOverlay>
              <FlightTile
                flight={activeFlight}
                sourceColumnId=""
                params={params}
                isDragOverlay={true}
              />
            </DragOverlay>,
            document.body
          )}
        </DndContext>
      </AccordionDetails>
    </Accordion>
  );
}

export default DispatchBoard;